import { useEffect, useRef, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "@/integrations/firebase/firestore";
import { logError } from "@/shared/services/ErrorReporting";

export interface TenantAdmin {
  uid: string;
  email: string;
}

const BATCH_SIZE = 10;

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

export function useTenantAdmins(adminUids: string[]) {
  const cacheKey = [...adminUids].sort().join("|");
  const adminUidsRef = useRef(adminUids);
  adminUidsRef.current = adminUids;

  const [admins, setAdmins] = useState<TenantAdmin[]>([]);
  const [isLoading, setIsLoading] = useState(adminUids.length > 0);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const uids = adminUidsRef.current;
    if (uids.length === 0) {
      setAdmins([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    (async () => {
      try {
        const usersRef = collection(db, "users");
        const batches = chunk(uids, BATCH_SIZE);
        const uidToEmail = new Map<string, string>();

        for (const batch of batches) {
          const q = query(usersRef, where("uid", "in", batch));
          const snap = await getDocs(q);
          snap.forEach((doc) => {
            const data = doc.data();
            if (
              typeof data.uid === "string" &&
              typeof data.email === "string"
            ) {
              uidToEmail.set(data.uid, data.email);
            }
          });
        }

        if (cancelled) return;

        const resolved: TenantAdmin[] = uids.map((uid) => ({
          uid,
          email: uidToEmail.get(uid) ?? "",
        }));

        setAdmins(resolved);
        setIsLoading(false);
      } catch (err) {
        if (cancelled) return;
        logError(err as Error);
        setAdmins([]);
        setError(err as Error);
        setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cacheKey]);

  return { admins, isLoading, error };
}
